import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api, type PlatformRole, type UserCreateInput, type UserRole, USER_SORT_KEYS, type UserSortKey } from "@/api";
import { usePlatformRoles } from "@/api/hooks/use-platform-roles";
import { userKeys, useUserPage } from "@/api/hooks/use-users";
import { AdminDetailModal } from "@/components/admin-detail-modal";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Select } from "@/components/ui/select";
import { serverDataTable, useUrlTable } from "@/lib/use-url-table";
import { UserDetailContent } from "./user-detail";
type CreateUserForm = UserCreateInput & {
  password: string;
};
const CREATE_ROLES: UserRole[] = ["admin", "seller", "manufacturer", "verified_seller", "shop-manager", "shop-product-manager", "shop-order-manager", "shop-support"];

/** Роли для фильтра списка: покупатели и все роли, которые создаёт консоль. */
const FILTER_ROLES: UserRole[] = ["buyer", ...CREATE_ROLES];
const initialCreateForm = (): CreateUserForm => ({
  email: "",
  phone: "",
  name: "",
  password: "",
  role: "admin",
  onboardingStatus: "active",
  platformRoleKey: null
});
export function UsersPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  // Страница, поиск, роль и сортировка — на сервере, срез — в адресной строке.
  const url = useUrlTable<UserSortKey>({
    filters: ["search", "role", "blocked"],
    defaultSort: {
      key: "created",
      dir: "desc"
    },
    defaultPageSize: 25,
    sortKeys: USER_SORT_KEYS
  });
  const blockedOnly = url.filters.blocked === "1";
  const usersQuery = useUserPage({
    search: url.filters.search || undefined,
    role: url.filters.role || undefined,
    blocked: blockedOnly || undefined,
    sort: url.sort.key,
    dir: url.sort.dir,
    page: url.page,
    pageSize: url.pageSize
  });
  // Счётчик на кнопке «Заблокированные» — total того же запроса с фильтром,
  // без строк: по странице посчитать всех заблокированных нельзя.
  const blockedCountQuery = useUserPage({
    blocked: true,
    page: 1,
    pageSize: 10
  });
  const users = usersQuery.data?.items ?? null;
  const table = serverDataTable(url, users ?? [], usersQuery.data?.total ?? 0);
  const [searchInput, setSearchInput] = useState(url.filters.search ?? "");
  const urlSearch = url.filters.search ?? "";
  useEffect(() => setSearchInput(urlSearch), [urlSearch]);
  const setFilterRef = useRef(url.setFilter);
  setFilterRef.current = url.setFilter;
  useEffect(() => {
    if (searchInput.trim() === urlSearch) return;
    const id = setTimeout(() => setFilterRef.current("search", searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput, urlSearch]);
  // Platform roles are optional context for the create dialog — a load
  // failure just leaves the select with the "none" option.
  const platformRolesQuery = usePlatformRoles();
  const platformRoles = platformRolesQuery.data ?? [];
  const error = usersQuery.error ? usersQuery.error.message : null;
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(() => initialCreateForm());
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const createUser = async () => {
    setCreateError(null);
    if (!createForm.email.trim() || !createForm.phone.trim() || !createForm.name.trim()) {
      setCreateError(t("users.create.required"));
      return;
    }
    setCreating(true);
    try {
      const {
        password,
        ...rest
      } = createForm;
      const created = await api.users.create({
        ...rest,
        email: rest.email.trim(),
        phone: rest.phone.trim(),
        name: rest.name.trim(),
        ...(password.trim() ? {
          password: password.trim()
        } : {})
      });
      await queryClient.invalidateQueries({
        queryKey: userKeys.all
      });
      setSelectedUserId(created.id);
      setCreateForm(initialCreateForm());
      setCreateOpen(false);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : t("users.create.failed"));
    } finally {
      setCreating(false);
    }
  };
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{error}
			</div>;
  if (users === null) return <p className="text-muted-foreground">{t("users.loading")}</p>;
  return <div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">{t("nav.users")}</h1>
					<p className="text-sm text-muted-foreground">{t("users.subtitle")}</p>
				</div>
				<Button onClick={() => setCreateOpen(true)}>
					<Plus className="size-4" />
					{t("users.create.button")}
				</Button>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<Input value={searchInput} onChange={e_0 => setSearchInput(e_0.target.value)} placeholder={t("users.searchPlaceholder")} aria-label={t("users.searchPlaceholder")} className="h-9 w-full sm:max-w-sm" />
				<Select value={url.filters.role ?? ""} aria-label={t("users.allRoles")} data-testid="users-filter-role" onChange={e_1 => url.setFilter("role", e_1.target.value)} className="w-full sm:w-56">
					<option value="">{t("users.allRoles")}</option>
					{FILTER_ROLES.map(role => <option key={role} value={role}>
							{t(`roles.${role}`, {
            defaultValue: role
          })}
						</option>)}
				</Select>
				<Button size="sm" variant={blockedOnly ? "default" : "outline"} aria-pressed={blockedOnly} data-testid="users-filter-blocked" onClick={() => url.setFilter("blocked", blockedOnly ? "" : "1")}>
					{t("users.filter.blocked", {
          count: blockedCountQuery.data?.total ?? 0
        })}
				</Button>
				{url.hasFilters && <Button size="sm" variant="ghost" onClick={() => {
        setSearchInput("");
        url.resetFilters();
      }}>
						{t("users.resetFilters")}
					</Button>}
			</div>

			{table.total === 0 ? <p className="text-muted-foreground">{url.hasFilters ? t("users.emptyFiltered") : t("users.empty")}</p> : <>
					<Table>
						<TableHeader>
							<TableRow>
								<SortableHead label={t("users.col.name")} sortKey="name" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("users.col.email")} sortKey="email" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("users.col.phone")} sortKey="phone" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("users.col.role")} sortKey="role" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("users.col.onboarding")} sortKey="onboarding" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("users.col.created")} sortKey="created" sort={table.sort} onToggle={table.toggleSort} />
							</TableRow>
						</TableHeader>
						<TableBody>
							{table.pageRows.map(u => <TableRow key={u.id} className="cursor-pointer" onClick={() => setSelectedUserId(u.id)}>
									<TableCell className="font-medium">
										<span className="flex flex-wrap items-center gap-1.5">
											{u.name || u.phone}
											{u.blockedAt && <Badge variant="destructive">{t("users.block.badge")}</Badge>}
										</span>
									</TableCell>
									<TableCell className="text-muted-foreground">
										{u.email.endsWith("@buyer.osmix.local") ? "—" : u.email}
									</TableCell>
									<TableCell className="text-muted-foreground">{u.phone}</TableCell>
									<TableCell>
										<Badge variant="secondary">{t(`roles.${u.role}`, {
                  defaultValue: u.role
                })}</Badge>
									</TableCell>
									<TableCell className="text-muted-foreground">
										{t(`onboarding.${u.onboardingStatus}`, {
                defaultValue: u.onboardingStatus
              })}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{new Date(u.createdAt).toLocaleDateString()}
									</TableCell>
								</TableRow>)}
						</TableBody>
					</Table>

					<DataTablePagination table={table} />
				</>}

			<AdminDetailModal open={!!selectedUserId} title={users.find(user => user.id === selectedUserId)?.name ?? t("nav.users")} fullPageHref={selectedUserId ? `/users/${selectedUserId}` : undefined} fullPageLabel={t("detailModal.openFullPage")} onOpenChange={open => !open && setSelectedUserId(null)}>
				{selectedUserId && <UserDetailContent id={selectedUserId} />}
			</AdminDetailModal>

			<CreateUserDialog open={createOpen} onOpenChange={open_0 => {
      setCreateOpen(open_0);
      if (!open_0) setCreateError(null);
    }} form={createForm} platformRoles={platformRoles} onFormChange={setCreateForm} onSubmit={createUser} creating={creating} error={createError} />
		</div>;
}
function CreateUserDialog(t0) {
  const $ = _c(144);
  const {
    open,
    onOpenChange,
    form,
    platformRoles,
    onFormChange,
    onSubmit,
    creating,
    error
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== form || $[1] !== onFormChange) {
    t1 = (key, value) => onFormChange({
      ...form,
      [key]: value
    });
    $[0] = form;
    $[1] = onFormChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const set = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("users.create.title");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <DialogTitle>{t2}</DialogTitle>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("users.create.description");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <DialogDescription>{t4}</DialogDescription>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <DialogHeader>{t3}{t5}</DialogHeader>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("users.field.name");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <Label htmlFor="create-user-name">{t7}</Label>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== set) {
    t9 = event => set("name", event.target.value);
    $[18] = set;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== form.name || $[21] !== t9) {
    t10 = <Input id="create-user-name" value={form.name} onChange={t9} />;
    $[20] = form.name;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <div className="grid gap-2">{t8}{t10}</div>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("users.field.email");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <Label htmlFor="create-user-email">{t12}</Label>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== set) {
    t14 = event_0 => set("email", event_0.target.value);
    $[30] = set;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== form.email || $[33] !== t14) {
    t15 = <Input id="create-user-email" type="email" value={form.email} onChange={t14} />;
    $[32] = form.email;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t13 || $[36] !== t15) {
    t16 = <div className="grid gap-2">{t13}{t15}</div>;
    $[35] = t13;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("users.field.phone");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <Label htmlFor="create-user-phone">{t17}</Label>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== set) {
    t19 = event_1 => set("phone", event_1.target.value);
    $[42] = set;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== form.phone || $[45] !== t19) {
    t20 = <Input id="create-user-phone" value={form.phone} onChange={t19} />;
    $[44] = form.phone;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t18 || $[48] !== t20) {
    t21 = <div className="grid gap-2">{t18}{t20}</div>;
    $[47] = t18;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t) {
    t22 = t("users.field.role");
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t22) {
    t23 = <Label htmlFor="create-user-role">{t22}</Label>;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const t24 = form.role;
  let t25;
  if ($[54] !== set) {
    t25 = event_2 => set("role", event_2.target.value as UserRole);
    $[54] = set;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = CREATE_ROLES.map(role => <option key={role} value={role}>{t(`roles.${role}`, {
        defaultValue: role
      })}</option>);
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== form.role || $[59] !== t25 || $[60] !== t26) {
    t27 = <Select id="create-user-role" value={t24} onChange={t25}>{t26}</Select>;
    $[58] = form.role;
    $[59] = t25;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t23 || $[63] !== t27) {
    t28 = <div className="grid gap-2">{t23}{t27}</div>;
    $[62] = t23;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t) {
    t29 = t("users.field.platformRole");
    $[65] = t;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t29) {
    t30 = <Label htmlFor="create-user-platform-role">{t29}</Label>;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  const t31 = form.platformRoleKey ?? "";
  let t32;
  if ($[69] !== set) {
    t32 = event_3 => set("platformRoleKey", event_3.target.value || null);
    $[69] = set;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== t) {
    t33 = t("users.platformRole.none");
    $[71] = t;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t33) {
    t34 = <option value="">{t33}</option>;
    $[73] = t33;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== platformRoles) {
    t35 = platformRoles.map(_temp);
    $[75] = platformRoles;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t31 || $[78] !== t32 || $[79] !== t34 || $[80] !== t35) {
    t36 = <Select id="create-user-platform-role" value={t31} onChange={t32}>{t34}{t35}</Select>;
    $[77] = t31;
    $[78] = t32;
    $[79] = t34;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t30 || $[83] !== t36) {
    t37 = <div className="grid gap-2">{t30}{t36}</div>;
    $[82] = t30;
    $[83] = t36;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  let t38;
  if ($[85] !== t) {
    t38 = t("users.create.password");
    $[85] = t;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== t38) {
    t39 = <Label htmlFor="create-user-password">{t38}</Label>;
    $[87] = t38;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  const t40 = form.password;
  let t41;
  if ($[89] !== set) {
    t41 = event_4 => set("password", event_4.target.value);
    $[89] = set;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] !== t) {
    t42 = t("users.password.show");
    $[91] = t;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  let t43;
  if ($[93] !== t) {
    t43 = t("users.password.hide");
    $[93] = t;
    $[94] = t43;
  } else {
    t43 = $[94];
  }
  let t44;
  if ($[95] !== form.password || $[96] !== t41 || $[97] !== t42 || $[98] !== t43) {
    t44 = <PasswordInput id="create-user-password" value={t40} onChange={t41} showLabel={t42} hideLabel={t43} />;
    $[95] = form.password;
    $[96] = t41;
    $[97] = t42;
    $[98] = t43;
    $[99] = t44;
  } else {
    t44 = $[99];
  }
  let t45;
  if ($[100] !== t) {
    t45 = t("users.create.passwordHint");
    $[100] = t;
    $[101] = t45;
  } else {
    t45 = $[101];
  }
  let t46;
  if ($[102] !== t45) {
    t46 = <p className="text-xs text-muted-foreground">{t45}</p>;
    $[102] = t45;
    $[103] = t46;
  } else {
    t46 = $[103];
  }
  let t47;
  if ($[104] !== t39 || $[105] !== t44 || $[106] !== t46) {
    t47 = <div className="grid gap-2">{t39}{t44}{t46}</div>;
    $[104] = t39;
    $[105] = t44;
    $[106] = t46;
    $[107] = t47;
  } else {
    t47 = $[107];
  }
  let t48;
  if ($[108] !== error) {
    t48 = error && <p className="text-sm text-destructive">{error}</p>;
    $[108] = error;
    $[109] = t48;
  } else {
    t48 = $[109];
  }
  let t49;
  if ($[110] !== t11 || $[111] !== t16 || $[112] !== t21 || $[113] !== t28 || $[114] !== t37 || $[115] !== t47 || $[116] !== t48) {
    t49 = <div className="grid gap-4 py-2">{t11}{t16}{t21}{t28}{t37}{t47}{t48}</div>;
    $[110] = t11;
    $[111] = t16;
    $[112] = t21;
    $[113] = t28;
    $[114] = t37;
    $[115] = t47;
    $[116] = t48;
    $[117] = t49;
  } else {
    t49 = $[117];
  }
  let t50;
  if ($[118] !== onOpenChange) {
    t50 = () => onOpenChange(false);
    $[118] = onOpenChange;
    $[119] = t50;
  } else {
    t50 = $[119];
  }
  let t51;
  if ($[120] !== t) {
    t51 = t("categories.cancel");
    $[120] = t;
    $[121] = t51;
  } else {
    t51 = $[121];
  }
  let t52;
  if ($[122] !== creating || $[123] !== t50 || $[124] !== t51) {
    t52 = <Button variant="outline" onClick={t50} disabled={creating}>{t51}</Button>;
    $[122] = creating;
    $[123] = t50;
    $[124] = t51;
    $[125] = t52;
  } else {
    t52 = $[125];
  }
  let t53;
  if ($[126] !== creating || $[127] !== t) {
    t53 = creating ? t("users.create.creating") : t("users.create.submit");
    $[126] = creating;
    $[127] = t;
    $[128] = t53;
  } else {
    t53 = $[128];
  }
  let t54;
  if ($[129] !== creating || $[130] !== onSubmit || $[131] !== t53) {
    t54 = <Button onClick={onSubmit} disabled={creating}>{t53}</Button>;
    $[129] = creating;
    $[130] = onSubmit;
    $[131] = t53;
    $[132] = t54;
  } else {
    t54 = $[132];
  }
  let t55;
  if ($[133] !== t52 || $[134] !== t54) {
    t55 = <DialogFooter>{t52}{t54}</DialogFooter>;
    $[133] = t52;
    $[134] = t54;
    $[135] = t55;
  } else {
    t55 = $[135];
  }
  let t56;
  if ($[136] !== t49 || $[137] !== t55 || $[138] !== t6) {
    t56 = <DialogContent className="sm:max-w-lg">{t6}{t49}{t55}</DialogContent>;
    $[136] = t49;
    $[137] = t55;
    $[138] = t6;
    $[139] = t56;
  } else {
    t56 = $[139];
  }
  let t57;
  if ($[140] !== onOpenChange || $[141] !== open || $[142] !== t56) {
    t57 = <Dialog open={open} onOpenChange={onOpenChange}>{t56}</Dialog>;
    $[140] = onOpenChange;
    $[141] = open;
    $[142] = t56;
    $[143] = t57;
  } else {
    t57 = $[143];
  }
  return t57;
}
function _temp(role_0) {
  return <option key={role_0.key} value={role_0.key}>{role_0.name}</option>;
}